import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HotelRestaurant } from '../types/restaurant';
import { restaurantService } from '../services/restaurantService';

function getHotelId(): string | null {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.hotel_id || null;
  } catch {
    return null;
  }
}

export default function RestaurantsPage() {
  const [restaurants, setRestaurants] = useState<HotelRestaurant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const hotelId = getHotelId();

  const loadRestaurants = () => {
    if (!hotelId) {
      setLoading(false);
      setError('Identifiant hôtel introuvable.');
      return;
    }
    setLoading(true);
    restaurantService
      .list(hotelId)
      .then(setRestaurants)
      .catch(() => setError('Erreur lors du chargement des restaurants.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadRestaurants(); }, [hotelId]);

  const handleDelete = async (rid: string, name: string) => {
    if (!hotelId) return;
    if (!window.confirm(`Supprimer le restaurant "${name}" ? Cette action est irréversible.`)) return;
    setDeleting(rid);
    try {
      await restaurantService.remove(hotelId, rid);
      setRestaurants((prev) => prev.filter((r) => r.id !== rid));
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la suppression.');
    } finally {
      setDeleting(null);
    }
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800',
    };
    const labels: Record<string, string> = {
      active: 'Actif',
      inactive: 'Inactif',
      suspended: 'Suspendu',
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <span className="ml-4 text-gray-600">Chargement des restaurants...</span>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Restaurants de l'Hôtel</h1>
          <p className="text-gray-600 mt-1">
            {restaurants.length} restaurant{restaurants.length !== 1 ? 's' : ''} rattaché{restaurants.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Link
          to="/admin/restaurants/new"
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter un restaurant
        </Link>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error}</div>
      )}

      {restaurants.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
          <p className="text-gray-500 text-lg">Aucun restaurant rattaché à cet hôtel.</p>
          <Link to="/admin/restaurants/new" className="mt-4 inline-block text-blue-600 hover:text-blue-800">
            Ajouter votre premier restaurant →
          </Link>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cuisine</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Capacité</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {restaurants.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900">{r.name}</div>
                    {r.commercial_name && <div className="text-sm text-gray-500">{r.commercial_name}</div>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{r.restaurant_type || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{r.cuisine_type || '—'}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{r.capacity > 0 ? `${r.capacity} places` : '—'}</td>
                  <td className="px-6 py-4">{statusBadge(r.status)}</td>
                  <td className="px-6 py-4 text-right space-x-2">
                    <Link to={`/admin/restaurants/${r.id}/edit`} className="text-blue-600 hover:text-blue-800 text-sm">
                      Modifier
                    </Link>
                    <button
                      onClick={() => r.id && handleDelete(r.id, r.name)}
                      disabled={deleting === r.id}
                      className="text-red-600 hover:text-red-800 text-sm disabled:opacity-50"
                    >
                      {deleting === r.id ? 'Suppression...' : 'Supprimer'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
